import { useState, useEffect } from "react";
import axios from "axios";
import { Search, Phone, Loader2, Check, Truck, CircuitBoard, Headphones, AlertTriangle, Calendar } from "lucide-react";
import { PCB_STAGES, prettyDate } from "@/lib/pcb";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FONT = { fontFamily: "'Space Grotesk',sans-serif" };
const fmtDT = d => d ? new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";

export default function PCBTrackPage() {
  const [input, setInput] = useState("");
  const [data, setData]   = useState(null);
  const [sc, setSC]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings`).then(r => setSC(r.data)).catch(() => {});
    axios.post(`${API}/analytics/event`, { page: "pcb-track", referrer: document.referrer || "" }).catch(() => {});
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) { setInput(ref); doSearch(ref); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doSearch = async (val) => {
    const q = (val ?? input).trim();
    if (!q) return;
    setLoading(true); setError(null); setData(null);
    try { const r = await axios.get(`${API}/pcb/track`, { params: { ref: q } }); setData(r.data); }
    catch (err) { setError(err.response?.status === 404 ? `No PCB replacement record found for "${q}". Please check the reference number.` : "Failed to load tracking info. Please try again."); }
    finally { setLoading(false); }
  };

  const cur = data?.current_stage_index ?? -1;

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50" data-testid="pcb-track-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <img src="/logo.jpg" alt="XSTRONG POWER" className="h-9 w-auto object-contain" />
          <div className="flex-1">
            <p className="font-bold text-sm text-slate-900" style={FONT}>XSTRONG POWER EV CHARGER</p>
            <p className="text-xs text-green-600 font-medium">PCB Replacement Tracking</p>
          </div>
          {sc?.phone && <a href={`tel:${sc.phone}`} className="hidden sm:flex items-center gap-1.5 text-xs text-slate-600 hover:text-green-700"><Phone size={13} />{sc.phone}</a>}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Search */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 mb-6">
          <h1 className="text-lg font-bold text-slate-900" style={FONT}>Track your PCB replacement</h1>
          <p className="text-sm text-slate-500 mt-0.5 mb-4">Enter the reference number shared by our team (e.g. <span className="font-mono">PCB-0001</span>).</p>
          <form onSubmit={e => { e.preventDefault(); doSearch(); }} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input value={input} onChange={e => setInput(e.target.value)} placeholder="PCB-0001" data-testid="pcb-track-input"
                className="w-full h-11 pl-9 pr-3 rounded-xl border border-slate-200 bg-white text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-green-500/40 focus:border-green-500" />
            </div>
            <button type="submit" disabled={loading || !input.trim()} data-testid="pcb-track-submit"
              className="h-11 px-5 rounded-xl bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-semibold flex items-center gap-2 transition-colors">
              {loading ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />}Track
            </button>
          </form>
          {error && <p className="mt-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 flex items-center gap-2" data-testid="pcb-track-error"><AlertTriangle size={14} />{error}</p>}
        </div>

        {loading && <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-500 text-sm"><Loader2 className="animate-spin mx-auto mb-2" />Loading…</div>}

        {data && (
          <>
            {/* Summary */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 mb-6" data-testid="pcb-track-result">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1">
                  <p className="text-xs text-slate-400">Reference</p>
                  <p className="text-2xl font-bold text-green-700 font-mono" style={FONT} data-testid="pcb-track-ref">{data.dispatch_no}</p>
                  <p className="text-sm text-slate-700 mt-0.5">Hello <strong>{data.customer_name}</strong>{data.job_ref && <span className="text-slate-400"> · Job {data.job_ref}</span>}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-400">Current status</p>
                  {cur >= 0 && <span className={`inline-block text-sm font-semibold px-3 py-1 rounded-lg border ${PCB_STAGES[cur].soft}`} data-testid="pcb-track-current">{data.current_stage_label}</span>}
                  {data.is_complete && <p className="text-xs text-green-700 mt-1">Replacement flow complete. Thank you!</p>}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4 pt-4 border-t border-slate-100 text-sm">
                <div className="flex items-start gap-2"><CircuitBoard size={15} className="text-green-600 mt-0.5" /><div><p className="text-xs text-slate-400">PCB sent</p><p className="font-medium text-slate-800">{data.pcb_model}</p><p className="text-xs text-slate-500">{data.pcb_voltage_current}{data.pcb_connector_type ? ` · ${data.pcb_connector_type}` : ""}</p></div></div>
                <div className="flex items-start gap-2"><Truck size={15} className="text-blue-600 mt-0.5" /><div><p className="text-xs text-slate-400">Courier to you</p><p className="font-medium text-slate-800">{data.courier_name || "Courier"}</p><p className="text-xs font-mono text-slate-600">{data.tracking_id}</p></div></div>
                <div className="flex items-start gap-2"><Calendar size={15} className="text-slate-500 mt-0.5" /><div><p className="text-xs text-slate-400">Dispatched on</p><p className="font-medium text-slate-800">{data.dispatch_date}</p>{data.return_tracking_id && <p className="text-xs text-amber-700 mt-0.5">Return: {data.return_courier_name ? `${data.return_courier_name} · ` : ""}<span className="font-mono">{data.return_tracking_id}</span></p>}</div></div>
              </div>
            </div>

            {/* Stepper */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="bg-green-700 px-5 py-3"><h2 className="text-white font-bold text-sm" style={FONT}>CUSTOMER VIEW <span className="font-normal opacity-80">(Order & Tracking Status)</span></h2></div>

              {/* Desktop horizontal */}
              <div className="hidden md:block px-6 py-8">
                <div className="relative flex items-start justify-between">
                  <div className="absolute top-6 left-[8%] right-[8%] h-0.5 bg-slate-200" />
                  <div className="absolute top-6 left-[8%] h-0.5 bg-green-500 transition-[width] duration-300" style={{ width: cur <= 0 ? 0 : `${(cur / 5) * 84}%` }} />
                  {data.stages.map((s, i) => {
                    const meta = PCB_STAGES[i]; const Icon = meta.icon; const done = s.completed;
                    return (
                      <div key={s.key} className="relative z-10 flex flex-col items-center text-center w-1/6 px-1" data-testid={`pcb-track-stage-${s.key}`}>
                        <div className={`h-12 w-12 rounded-full flex items-center justify-center border-2 ${done ? `${meta.bg} border-transparent text-white shadow-md` : i === cur + 1 ? "bg-white border-green-500 text-green-600" : "bg-white border-slate-200 text-slate-300"}`}>
                          {done ? <Check size={22} strokeWidth={3} /> : <Icon size={20} />}
                        </div>
                        <p className={`text-sm font-bold mt-3 leading-tight ${done ? "text-slate-900" : "text-slate-400"}`}>{s.label}</p>
                        <p className={`text-xs mt-1 ${done ? "text-slate-700 font-medium" : "text-slate-400"}`}>{done ? prettyDate(s.date) : "Pending"}</p>
                        <p className="text-[11px] text-slate-500 mt-1 leading-snug">{done ? s.message : s.hint}</p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Mobile vertical */}
              <ol className="md:hidden px-5 py-5">
                {data.stages.map((s, i) => {
                  const meta = PCB_STAGES[i]; const Icon = meta.icon; const done = s.completed;
                  return (
                    <li key={s.key} className="relative flex gap-4 pb-6 last:pb-0">
                      {i < 5 && <span className={`absolute left-[19px] top-10 bottom-0 w-0.5 ${done && data.stages[i + 1]?.completed ? "bg-green-500" : "bg-slate-200"}`} />}
                      <div className={`relative z-10 h-10 w-10 rounded-full flex items-center justify-center border-2 flex-shrink-0 ${done ? `${meta.bg} border-transparent text-white` : i === cur + 1 ? "bg-white border-green-500 text-green-600" : "bg-white border-slate-200 text-slate-300"}`}>
                        {done ? <Check size={18} strokeWidth={3} /> : <Icon size={17} />}
                      </div>
                      <div className="pt-1.5">
                        <p className={`text-sm font-bold ${done ? "text-slate-900" : "text-slate-400"}`}>{s.label}</p>
                        <p className={`text-xs ${done ? "text-slate-700 font-medium" : "text-slate-400"}`}>{done ? prettyDate(s.date) : "Pending"}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{done ? s.message : s.hint}</p>
                      </div>
                    </li>
                  );
                })}
              </ol>
              <p className="px-5 pb-4 text-[11px] text-slate-400">Last updated {fmtDT(data.updated_at)}</p>
            </div>

            {/* Help */}
            <div className="mt-6 bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="h-11 w-11 rounded-full bg-green-50 text-green-700 flex items-center justify-center flex-shrink-0"><Headphones size={20} /></div>
              <div className="flex-1">
                <p className="text-sm font-bold text-slate-900" style={FONT}>Need Help?</p>
                <p className="text-xs text-slate-500">Once you replace the PCB, please send the old charger back and share the return tracking ID with us.</p>
              </div>
              {sc?.phone && <a href={`https://wa.me/${sc.phone.replace(/\D/g, "").replace(/^(\d{10})$/, "91$1")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-green-600 hover:bg-green-700 text-white text-sm font-semibold" data-testid="pcb-track-whatsapp"><Phone size={15} />WhatsApp / Call: {sc.phone}</a>}
            </div>
          </>
        )}

        {!data && !loading && !error && (
          <div className="text-center text-slate-400 text-sm py-10">
            <CircuitBoard className="w-10 h-10 mx-auto mb-2 text-slate-300" />Enter your reference number above to see live status.
          </div>
        )}
      </div>
      <footer className="text-center text-xs text-slate-400 py-6">{sc?.name || "XSTRONG POWER"} · Customer Support · Genuine Parts · Sustainable Future</footer>
    </div>
  );
}
